import { API_BASE_URL } from 'config';

const safeStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    if (import.meta?.env?.DEV) console.warn(`[authFetch] localStorage blocked; cannot read ${key}`, err);
    return null;
  }
};

const safeStorageSet = (key, value) => {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (err) {
    if (import.meta?.env?.DEV) console.warn(`[authFetch] localStorage blocked; cannot write ${key}`, err);
  }
};

const safeStorageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    if (import.meta?.env?.DEV) console.warn(`[authFetch] localStorage blocked; cannot remove ${key}`, err);
  }
};

export const getAccessToken = () => safeStorageGet('authToken');
export const getRefreshToken = () => safeStorageGet('refreshToken');
export const setAccessToken = (token) => safeStorageSet('authToken', token);
export const setRefreshToken = (token) => safeStorageSet('refreshToken', token);
export const clearTokens = () => {
  safeStorageRemove('authToken');
  safeStorageRemove('refreshToken');
};

export const extractTokens = (responseJson) => {
  const accessToken =
    responseJson?.tokens?.accessToken ??
    responseJson?.tokens?.AccessToken ??
    responseJson?.accessToken ??
    responseJson?.AccessToken ??
    responseJson?.token ??
    responseJson?.Token ??
    null;

  const refreshToken =
    responseJson?.tokens?.refreshToken ??
    responseJson?.tokens?.RefreshToken ??
    responseJson?.refreshToken ??
    responseJson?.RefreshToken ??
    null;

  return { accessToken, refreshToken };
};

const readJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  // Backend: POST /api/User/refresh.
  // In your backend, refresh token is stored as HttpOnly cookie on login.
  // If we don't have a JS-readable refresh token, we still attempt a cookie-based refresh.
  const url = `${API_BASE_URL}/api/User/refresh`;
  const bodies = refreshToken ? [{ refreshToken }, { RefreshToken: refreshToken }] : [{}, null];

  for (const bodyObj of bodies) {
    const init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: bodyObj == null ? null : JSON.stringify(bodyObj)
    };

    // If body is null, drop the Content-Type header to avoid some servers rejecting an empty JSON body.
    if (init.body == null) delete init.headers['Content-Type'];

    const response = await fetch(url, init);

    if (!response.ok) continue;

    const json = await readJsonSafe(response);
    if (!json) continue;

    const tokens = extractTokens(json);
    if (tokens.accessToken) setAccessToken(tokens.accessToken);
    // Backend sets refresh token as HttpOnly cookie and does not return it in JSON.
    return tokens.accessToken || null;
  }

  return null;
};

export const authFetch = async (url, options = {}, { retryOnUnauthorized = true } = {}) => {
  const headers = new Headers(options.headers || {});

  if (!headers.has('Content-Type') && options.body != null) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getAccessToken();
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(url, { credentials: 'include', ...options, headers });

  if (retryOnUnauthorized && (response.status === 401 || response.status === 403)) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers.set('Authorization', `Bearer ${newToken}`);
      return await fetch(url, { credentials: 'include', ...options, headers });
    }
  }

  return response;
};
